import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import config from '../../config/config';
import DashboardLayout from '../shared/layout/DashboardLayout';
import DataTable from 'react-data-table-component';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { FaEye, FaTrash, FaLock, FaUndo, FaCheck, FaArchive } from "react-icons/fa";
import Modal from '../../components/common/Modal';
import { Tooltip } from 'react-tooltip';
import { getAdminMenuItems, getSupervisorMenuItems } from '../../utils/menuItems';
import '../../styles/App.css';
import Select from 'react-select';
import UnsealProgressModal from '../../components/UnsealProgressModal';

const DataAdmin = () => {
    const [dataList, setDataList] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [username, setUsername] = useState('');
    const [searchText, setSearchText] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEncryptModal, setShowEncryptModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [ticketNoToDelete, setTicketNoToDelete] = useState(null);
    const [ticketNoToEncrypt, setTicketNoToEncrypt] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();
    const [totalData, setTotalData] = useState(0);
    const [showAdvanceFilter, setShowAdvanceFilter] = useState(false);
    const [activeFilters, setActiveFilters] = useState([]);
    const [userRole, setUserRole] = useState(null);
    const [showSealModal, setShowSealModal] = useState(false);
    const [sealTicketNo, setSealTicketNo] = useState('');
    const [sealNamaIgt, setSealNamaIgt] = useState('');
    
    // State untuk perubahan status khusus
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusChangeData, setStatusChangeData] = useState({
        ticketNo: '',
        currentStatus: '',
        newStatus: '',
        actionType: '' // 'reset' atau 'complete'
    });
    
    // State untuk popup dropdown
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
    const [selectedRowData, setSelectedRowData] = useState(null);

    axios.interceptors.response.use(
        response => response,
        error => {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('token');
                navigate('/', { state: { sessionExpired: true } });
            }
            return Promise.reject(error);
        }
    );

    const applyFilters = useCallback((data) => {
        return data.filter((row) => {
            return activeFilters.every((filter) => {
                const rowValue = row[filter.key]?.toString().toLowerCase() || '';
                return rowValue.includes(filter.value.toLowerCase());
            });
        });
    }, [activeFilters]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/');
                return;
            }

            const decoded = jwtDecode(token);
            if (!decoded.twoFactorVerified) {
                navigate('/');
                return;
            }

            if (decoded.role !== 0 && decoded.role !== 2) {
                navigate('/');
                return;
            }

            setUsername(decoded.username);
            setUserRole(decoded.role);

            const response = await axios.get(`${config.API_URL}/dataAdmin`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            const sortedData = response.data.sort(
                (a, b) => new Date(b.last_modified) - new Date(a.last_modified)
            );
            setDataList(sortedData);
            setFilteredData(sortedData);
            setTotalData(sortedData.length);
        } catch (err) {
            setError('Gagal memuat data.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Effect untuk menutup dropdown saat klik di luar
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Cek apakah klik di dalam dropdown
            const dropdown = document.querySelector('.status-dropdown');
            if (dropdown && dropdown.contains(event.target)) {
                return; // Jangan tutup jika klik di dalam dropdown
            }
            
            if (showStatusDropdown) {
                setShowStatusDropdown(false);
                setSelectedRowData(null);
            }
        };

        if (showStatusDropdown) {
            // Delay sedikit untuk menghindari immediate close
            setTimeout(() => {
                document.addEventListener('click', handleClickOutside);
            }, 100);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showStatusDropdown]);

    useEffect(() => {
        const filteredAndSearchedData = applyFilters(filteredData);
        const sortedData = [...filteredAndSearchedData].sort((a, b) =>
            new Date(b.created_date) - new Date(a.created_date)
        );
        setTotalData(sortedData.length);
    }, [filteredData, activeFilters, applyFilters]);

    const handleSearch = () => {
        setSearchLoading(true);
        setTimeout(() => {
            const query = searchText.toLowerCase();
            if (query) {
                const filtered = dataList.filter((row) =>
                    row.no_tiket.toLowerCase().includes(query) ||
                    row.nama_igt.toLowerCase().includes(query) ||
                    row.kl.toLowerCase().includes(query) ||
                    row.jenis_igt?.toLowerCase().includes(query) ||
                    row.status.toLowerCase().includes(query) ||
                    row.verifikator?.toLowerCase().includes(query)
                );
                const sortedFilteredData = filtered.sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified));
                setFilteredData(sortedFilteredData);
            } else {
                const sortedData = dataList.sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified));
                setFilteredData(sortedData);
            }
            setSearchLoading(false);
        }, 500);
    };

    // Handler untuk enkripsi dengan UnsealProgressModal
    const handleEncryptClick = (ticketNo, namaIgt) => {
        setSealTicketNo(ticketNo);
        setSealNamaIgt(namaIgt || '');
        setShowSealModal(true);
    };

    // Handler ketika seal modal selesai
    const handleSealComplete = (result) => {
        console.log('Seal completed:', result);
        setShowSealModal(false);
        setSealTicketNo('');
        setSealNamaIgt('');

        // Refresh data setelah enkripsi selesai
        fetchData();

        // Tampilkan pesan sukses
        if (result && result.status === 'queued') {
            setSuccessMessage('Proses enkripsi sedang berjalan di background. File akan otomatis terenkripsi ketika proses selesai.');
        } else {
            setSuccessMessage('File berhasil dienkripsi dan disimpan dengan aman!');
        }
        setShowSuccessModal(true);
    };

    // Handler untuk menutup seal modal
    const handleSealModalClose = () => {
        setShowSealModal(false);
        setSealTicketNo('');
        setSealNamaIgt('');
    };

    // Handler enkripsi 
    const handleEncryptConfirm = async () => {
        if (!ticketNoToEncrypt) return;
        setShowEncryptModal(false);

        try {
            const token = localStorage.getItem('token');
            await axios.post(`${config.API_URL}/dataAdmin/seal/${ticketNoToEncrypt}`, {}, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            setSuccessMessage(`Permintaan enkripsi untuk tiket ${ticketNoToEncrypt} telah ditambahkan ke antrian.`);
            setShowSuccessModal(true);
            setTicketNoToEncrypt(null);
            fetchData();

        } catch (error) {
            console.error('Error saat mengirim permintaan enkripsi:', error);
            setError(error.response?.data?.message || 'Gagal mengirim permintaan ke server.');
        }
    };

    const handleDeleteClick = (ticketNo) => {
        setTicketNoToDelete(ticketNo);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${config.API_URL}/delete/data`, { ticketNo: ticketNoToDelete }, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            setDataList(prevData => prevData.filter(item => item.no_tiket !== ticketNoToDelete));
            setFilteredData(prevData => prevData.filter(item => item.no_tiket !== ticketNoToDelete));
            setShowDeleteModal(false);
            setTicketNoToDelete(null);
            setSuccessMessage('Data berhasil dihapus!');
            setShowSuccessModal(true);
        } catch (error) {
            console.error('Error delete ticket:', error);
        }
    };

    // Handler untuk klik baris - menampilkan dropdown
    const handleRowClick = (row, event) => {
        // Tutup dropdown yang sedang terbuka terlebih dahulu
        if (showStatusDropdown) {
            setShowStatusDropdown(false);
            setSelectedRowData(null);
            // Delay sedikit sebelum membuka yang baru
            setTimeout(() => {
                openDropdownForRow(row, event);
            }, 150);
        } else {
            openDropdownForRow(row, event);
        }
    };

    // Fungsi terpisah untuk membuka dropdown
    const openDropdownForRow = (row, event) => {
        // Gunakan event mouse untuk posisi, atau fallback ke posisi default
        let x = event && event.clientX ? event.clientX : window.innerWidth / 2;
        let y = event && event.clientY ? event.clientY : window.innerHeight / 2;
        
        // Pastikan dropdown tidak keluar dari viewport
        const dropdownWidth = 320; // max-width dari CSS
        const dropdownHeight = 250; // estimasi tinggi dropdown yang lebih akurat
        
        if (x + dropdownWidth > window.innerWidth) {
            x = window.innerWidth - dropdownWidth - 20;
        }
        if (y + dropdownHeight > window.innerHeight) {
            y = y - dropdownHeight - 20;
        }
        
        // Pastikan posisi tidak negatif
        x = Math.max(20, x);
        y = Math.max(20, y);
        
        setDropdownPosition({ x, y });
        setSelectedRowData(row);
        setShowStatusDropdown(true);
    };

    // Handler untuk menutup dropdown
    const handleCloseDropdown = () => {
        setShowStatusDropdown(false);
        setSelectedRowData(null);
    };

    // Handler untuk pilihan status dari dropdown
    const handleStatusOptionClick = (newStatus, actionType) => {
        setStatusChangeData({
            ticketNo: selectedRowData.no_tiket,
            currentStatus: selectedRowData.status,
            newStatus,
            actionType
        });
        setShowStatusModal(true);
        setShowStatusDropdown(false);
    };

    // Konfirmasi perubahan status
    const handleStatusChangeConfirm = async () => {
        try {
            const token = localStorage.getItem('token');
            
            // Endpoint khusus untuk perubahan status admin tanpa notifikasi
            await axios.post(`${config.API_URL}/dataAdmin/changeStatus`, {
                ticketNo: statusChangeData.ticketNo,
                newStatus: statusChangeData.newStatus,
                skipNotification: true // Flag untuk tidak mengirim notifikasi
            }, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            });

            // Update data di state
            const updateData = (data) => data.map(item => 
                item.no_tiket === statusChangeData.ticketNo 
                    ? { ...item, status: statusChangeData.newStatus, last_modified: new Date().toISOString() }
                    : item
            );

            setDataList(updateData);
            setFilteredData(updateData);
            
            setShowStatusModal(false);
            setStatusChangeData({
                ticketNo: '',
                currentStatus: '',
                newStatus: '',
                actionType: ''
            });

            const actionText = statusChangeData.actionType === 'reset' ? 'direset ke Belum Diverifikasi' : 'diselesaikan menjadi Sudah Terintegrasi';
            setSuccessMessage(`Status tiket ${statusChangeData.ticketNo} berhasil ${actionText}!`);
            setShowSuccessModal(true);
            
        } catch (error) {
            console.error('Error changing status:', error);
            setError(error.response?.data?.message || 'Gagal mengubah status tiket.');
        }
    };

    const ValueFilterDropdown = ({ column, data, onSelect }) => {
        const uniqueValues = [...new Set(data.map((row) => row[column]?.toString() || ''))].filter(Boolean);
        const options = uniqueValues.map((value) => ({
            value,
            label: value,
        }));

        return (
            <Select
                options={options}
                placeholder={`Pilih nilai untuk ${column}`}
                isSearchable
                onChange={(selectedOption) => onSelect(selectedOption?.value || '')}
                styles={{
                    control: (provided) => ({
                        ...provided,
                        width: '250px',
                    }),
                }}
            />
        );
    };

    const AddFilter = ({ onAddFilter, data }) => {
        const [selectedColumn, setSelectedColumn] = useState('');
        const [filterValue, setFilterValue] = useState('');

        const columns = [
            { key: 'no_tiket', label: 'No Tiket' },
            { key: 'nama_igt', label: 'Nama IGT' },
            { key: 'kl', label: 'KL' },
            { key: 'jenis_igt', label: 'Jenis IGT' },
            { key: 'status', label: 'Status' },
            { key: 'verifikator', label: 'Verifikator' },
            { key: 'last_modified', label: 'Tanggal Update' },
            { key: 'created_date', label: 'Tanggal Pengajuan' },
        ];

        const handleAddFilter = () => {
            if (selectedColumn && filterValue) {
                onAddFilter({ key: selectedColumn, value: filterValue });
                setSelectedColumn('');
                setFilterValue('');
            }
        };

        return (
            <div className="add-filter-container mb-3 d-flex align-items-center gap-2">
                <select
                    className="form-select"
                    value={selectedColumn}
                    onChange={(e) => setSelectedColumn(e.target.value)}
                >
                    <option value="">Pilih Kolom</option>
                    {columns.map((col) => (
                        <option key={col.key} value={col.key}>
                            {col.label}
                        </option>
                    ))}
                </select>

                {selectedColumn && (
                    <ValueFilterDropdown
                        column={selectedColumn}
                        data={data}
                        onSelect={(value) => setFilterValue(value)}
                    />
                )}

                <button className="btn btn-primary d-flex align-items-center" onClick={handleAddFilter}>
                    <i className="fas fa-plus me-2"></i>
                </button>
                <button
                    className="btn btn-warning align-items-center"
                    onClick={() => setActiveFilters([])}
                    disabled={activeFilters.length === 0}
                >
                    <i className="fas fa-sync-alt me-2"></i>
                </button>
            </div>
        );
    };

    const handleAddFilter = (newFilter) => {
        setActiveFilters((prevFilters) => [...prevFilters, newFilter]);
    };

    const handleRemoveFilter = (index) => {
        setActiveFilters((prevFilters) => prevFilters.filter((_, i) => i !== index));
    };

    // Fungsi untuk menentukan URL berdasarkan status dan role
    const getViewUrl = (row) => {
        const encodedTicketNo = btoa(row.no_tiket);

        if (row.status === 'Sudah Terintegrasi') {
            // Untuk status "Sudah Terintegrasi", semua role mengakses URL yang sama
            return `/rekapdata/${encodedTicketNo}`;
        } else if (row.status === 'Belum Diverifikasi') {
            // Untuk status "Belum Diverifikasi", URL berbeda berdasarkan role
            return userRole === 0
                ? `/admin/verify/${encodedTicketNo}`
                : `/supervisor/approval/${encodedTicketNo}`;
        } else {
            // Untuk status lainnya, URL berbeda berdasarkan role
            return userRole === 0
                ? `/admin/verify/${encodedTicketNo}`
                : `/supervisor/approval/${encodedTicketNo}`;
        }
    };

    const columns = [
        {
            name: 'No Tiket',
            selector: row => row.no_tiket,
            sortable: true,
            width: '200px',
            cell: row => (
                <div>
                    {row.no_tiket.length > 20 ? `${row.no_tiket.substring(0, 20)}...` : row.no_tiket}
                </div>
            )
        },
        {
            name: 'Pengajuan',
            selector: row => row.created_date,
            sortable: true,
            width: '110px',
            cell: row => {
                if (!row.created_date) return '-';
                const date = new Date(row.created_date);
                if (isNaN(date.getTime())) return '-';
                return date.toLocaleDateString('id-ID', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                });
            },
            sortFunction: (rowA, rowB, direction) => {
                if (!rowA.created_date && !rowB.created_date) return 0;
                if (!rowA.created_date) return direction === 'asc' ? -1 : 1;
                if (!rowB.created_date) return direction === 'asc' ? 1 : -1;
                const dateA = new Date(rowA.created_date);
                const dateB = new Date(rowB.created_date);
                if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
                if (isNaN(dateA.getTime())) return direction === 'asc' ? -1 : 1;
                if (isNaN(dateB.getTime())) return direction === 'asc' ? 1 : -1;
                return direction === 'asc'
                    ? dateA.getTime() - dateB.getTime()
                    : dateB.getTime() - dateA.getTime();
            }
        },
        {
            name: 'Nama IGT',
            selector: row => row.nama_igt,
            sortable: true,
            width: '200px',
            cell: row => (
                <div>
                    {row.nama_igt.length > 100 ? `${row.nama_igt.substring(0, 100)}...` : row.nama_igt}
                </div>
            )
        },
        {
            name: 'KL',
            selector: row => row.kl,
            sortable: true,
            width: '150px',
            cell: row => (
                <div>
                    {row.kl.length > 50 ? `${row.kl.substring(0, 50)}...` : row.kl}
                </div>
            )
        },
        {
            name: 'Jenis IGT',
            selector: row => row.jenis_igt || '-',
            sortable: true,
            width: '150px',
            cell: row => (
                <div data-tooltip-id="jenis-igt-tooltip" data-tooltip-content={row.jenis_igt || '-'}>
                    {row.jenis_igt && row.jenis_igt.length > 30 ? `${row.jenis_igt.substring(0, 30)}...` : row.jenis_igt || '-'}
                </div>
            )
        },
        {
            name: 'Verifikator',
            selector: row => row.verifikator || '-',
            sortable: true,
            width: '110px',
            cell: row => (
                <div>
                    {row.verifikator && row.verifikator.length > 30 ? `${row.verifikator.substring(0, 30)}...` : row.verifikator || '-'}
                </div>
            )
        },
        {
            name: 'Status',
            selector: row => row.status,
            width: '150px',
            sortable: true,
            cell: row => <span className={`badge status-${row.status.toLowerCase().replace(/ /g, '-')}`}>{row.status}</span>,
        },
        {
            name: 'Status Publikasi',
            selector: row => row.status_publikasi || 'Belum Publikasi',
            width: '150px',
            sortable: true,
            cell: row => {
                const statusPublikasi = row.status_publikasi || 'Belum Publikasi';
                let badgeClass = 'status-sedang-verifikasi';
                if (statusPublikasi === 'Sudah Publikasi') {
                    badgeClass = 'status-sudah-terintegrasi';
                } else if (statusPublikasi === 'Siap Publikasi') {
                    badgeClass = 'status-perlu-perbaikan';
                } else {
                    badgeClass = 'status-belum-diverifikasi';
                }
                return (
                    <span className={`badge ${badgeClass}`}>
                        {statusPublikasi}
                    </span>
                );
            },
        },
        {
            name: 'Update',
            selector: row => row.last_modified,
            sortable: true,
            width: '110px',
            cell: row => {
                if (!row.last_modified) return '-';
                const date = new Date(row.last_modified);
                if (isNaN(date.getTime())) return '-';
                
                return (
                    <div data-tooltip-id="update-tooltip"
                        data-tooltip-content={`Terakhir diupdate: ${date.toLocaleDateString('id-ID')}`}>
                        {date.toLocaleDateString('id-ID', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                        })}
                    </div>
                );
            },
            sortFunction: (rowA, rowB, direction) => {
                if (!rowA.last_modified && !rowB.last_modified) return 0;
                if (!rowA.last_modified) return direction === 'asc' ? -1 : 1;
                if (!rowB.last_modified) return direction === 'asc' ? 1 : -1;
                const dateA = new Date(rowA.last_modified);
                const dateB = new Date(rowB.last_modified);
                if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
                if (isNaN(dateA.getTime())) return direction === 'asc' ? -1 : 1;
                if (isNaN(dateB.getTime())) return direction === 'asc' ? 1 : -1;
                return direction === 'asc'
                    ? dateA.getTime() - dateB.getTime()
                    : dateB.getTime() - dateA.getTime();
            }
        },
        {
            name: 'Aksi',
            cell: row => {
                const isProcessing = row.status_enkripsi === 'queuing' || row.status_enkripsi === 'processing';
                const isSealed = row.status_enkripsi === 'sealed';
                let encryptionStatusInfo = null;
                if (isProcessing) {
                    const statusText = row.status_enkripsi === 'queuing' ? 'Dalam Antrian' : 'Memproses...';
                    encryptionStatusInfo = <span className={`status-badge status-${row.status_enkripsi}`}>{statusText}</span>;
                } else if (row.status_enkripsi === 'failed') {
                    encryptionStatusInfo = <span className="status-badge status-failed">Gagal</span>;
                }

                return (
                    <div className="actions">
                        <Link to={getViewUrl(row)} className="action-button view-button" onClick={(e) => {
                            if (e && e.stopPropagation) {
                                e.stopPropagation();
                            }
                        }}>
                            <FaEye /> Lihat
                        </Link>
                        
                        <button className="action-button delete-button" onClick={(e) => {
                            if (e && e.stopPropagation) {
                                e.stopPropagation();
                            }
                            handleDeleteClick(row.no_tiket);
                        }}>
                            <FaTrash /> Hapus
                        </button>
                    </div>
                );
            },
            width: '200px',
        },
    ];

    const customSort = (rows, selector, direction) => {
        return rows.sort((a, b) => {
            const aField = selector(a) || '';
            const bField = selector(b) || '';
            // Jika keduanya adalah tanggal, bandingkan langsung sebagai tanggal
            if (selector.name === 'created_date') {
                if (!aField || !bField) {
                    if (!aField && !bField) return 0;
                    if (!aField) return direction === 'asc' ? 1 : -1;
                    return direction === 'asc' ? -1 : 1;
                }
                const aDate = new Date(aField);
                const bDate = new Date(bField);

                if (direction === 'asc') {
                    return aDate - bDate;
                } else {
                    return bDate - aDate;
                }
            }
            if (direction === 'asc') {
                return aField.toString().localeCompare(bField.toString());
            } else {
                return bField.toString().localeCompare(aField.toString());
            }
        });
    };

    const menuItems = userRole === 2 ? getSupervisorMenuItems() : getAdminMenuItems();
    const roleLabel = userRole === 2 ? "Supervisor" : "Admin";

    return (
        <DashboardLayout menuItems={menuItems} userRole={roleLabel}>
            <div className="container mt-4">
                <nav aria-label="breadcrumb" className="mb-3">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item"><a href="/admin">Dashboard</a></li>
                        <li className="breadcrumb-item active" aria-current="page">Daftar Data</li>
                    </ol>
                </nav>

                <div className="mb-3 d-flex justify-content-between align-items-center">
                    <div className="search-container">
                        <input
                            type="text"
                            className="filter-input"
                            placeholder="Cari No Tiket, Nama, KL, atau Status"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <button className="btn btn-primary search-button"
                            onClick={handleSearch}
                            disabled={searchLoading}
                            data-tooltip-id="cari-tooltip"
                            data-tooltip-content="Cari Data"
                        >
                            <i className="fas fa-search"></i>
                        </button>
                        <Tooltip id="cari-tooltip" />
                        <button
                            className="btn btn-secondary align-items-center"
                            onClick={() => setShowAdvanceFilter(!showAdvanceFilter)}
                            data-tooltip-id="advance-filter-tooltip"
                            data-tooltip-content="Filter Lanjutan"
                        >
                            <i className="fas fa-filter"></i>
                        </button>
                        <Tooltip id="advance-filter-tooltip" />
                    </div>
                </div>

                {showAdvanceFilter && (
                    <>
                        <AddFilter onAddFilter={handleAddFilter} data={dataList} />
                        <div className="active-filters">
                            {activeFilters.map((filter, index) => (
                                <div key={index} className="filter-badge">
                                    <span>
                                        {filter.key}: {filter.value}
                                    </span>
                                    <button
                                        className="btn btn-sm btn-danger ms-2"
                                        onClick={() => handleRemoveFilter(index)}
                                    >
                                        &times;
                                    </button>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <div className="card shadow-lg p-4 printable">
                    <h3 className="title">Daftar Data</h3>

                    {error && <div className="alert-danger">{error}</div>}
                    <DataTable
                        columns={columns}
                        data={applyFilters(filteredData)}
                        progressPending={loading}
                        progressComponent={<LoadingSpinner message="Memuat data..." type="spinner" size="medium" />}
                        pagination
                        highlightOnHover
                        pointerOnHover
                        defaultSortFieldId="created_date"
                        defaultSortAsc={false}
                        sortFunction={customSort}
                        noDataComponent={<p className="text-center text-muted">Tidak ada data yang tersedia.</p>}
                        responsive
                        onRowClicked={handleRowClick}
                        conditionalRowStyles={[
                            {
                                when: row => {
                                    return row.is_published === false || !row.is_published;
                                },
                                style: {
                                    backgroundColor: '#fef3c7',
                                    borderLeft: '4px solid #fbbf24'
                                }
                            }
                        ]}
                    />
                </div>
                <Tooltip id="ticket-tooltip" />
                <Tooltip id="nama-igt-tooltip" />
                <Tooltip id="kl-tooltip" />
                <Tooltip id="verifikator-tooltip" />
                <Tooltip id="jenis-igt-tooltip" />
                <Tooltip id="update-tooltip" />
                
                {/* Status Change Dropdown */}
                {showStatusDropdown && selectedRowData && (
                    <>
                        <div 
                            className="dropdown-backdrop"
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                backdropFilter: 'blur(2px)',
                                zIndex: 999
                            }}
                        />
                        <div 
                            className="status-dropdown"
                            style={{
                                position: 'fixed',
                                top: dropdownPosition.y,
                                left: dropdownPosition.x,
                                zIndex: 1001
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                        <div className="dropdown-header">
                            <h6>Ubah Status Tiket</h6>
                            <small>{selectedRowData.no_tiket}</small>
                        </div>
                        <div className="dropdown-content">
                            <div className="current-status">
                                <span>Status saat ini: <strong>{selectedRowData.status}</strong></span>
                            </div>
                            <div className="status-options">
                                {selectedRowData.status !== 'Belum Diverifikasi' && (
                                    <button 
                                        className="status-option reset-option"
                                        onClick={() => handleStatusOptionClick('Belum Diverifikasi', 'reset')}
                                    >
                                        <FaUndo /> Reset ke Belum Diverifikasi
                                    </button>
                                )}
                                {selectedRowData.status !== 'Sudah Terintegrasi' && (
                                    <button 
                                        className="status-option complete-option"
                                        onClick={() => handleStatusOptionClick('Sudah Terintegrasi', 'complete')}
                                    >
                                        <FaCheck /> Selesaikan ke Sudah Terintegrasi
                                    </button>
                                )}
                            </div>
                        </div>
                        </div>
                    </>
                )}

            {searchLoading && (
                <div className="loading-overlay-fullscreen">
                    <LoadingSpinner message="Mencari data..." type="dots" size="large" />
                </div>
            )}

            {/* Modal Konfirmasi Enkripsi */}
            <Modal
                isOpen={showEncryptModal}
                onClose={() => setShowEncryptModal(false)}
                type="confirm"
                title="Konfirmasi Enkripsi"
                message={`Anda yakin ingin mengenkripsi data peta untuk tiket No ${ticketNoToEncrypt}?`}
                onConfirm={handleEncryptConfirm}
            />

            {/* Modal Konfirmasi Hapus */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                type="confirm"
                title="Konfirmasi Hapus"
                message={`Apakah Anda yakin ingin menghapus tiket No ${ticketNoToDelete}? Tindakan ini tidak dapat dibatalkan.`}
                onConfirm={handleDeleteConfirm}
            />

            {/* Modal Konfirmasi Perubahan Status */}
            <Modal
                isOpen={showStatusModal}
                onClose={() => setShowStatusModal(false)}
                type="confirm"
                title="Konfirmasi Perubahan Status"
                message={`Anda yakin ingin mengubah status tiket ${statusChangeData.ticketNo} dari "${statusChangeData.currentStatus}" menjadi "${statusChangeData.newStatus}"? Perubahan ini tidak akan mengirim notifikasi kepada pengguna.`}
                onConfirm={handleStatusChangeConfirm}
            />

            {/* Modal Sukses */}
            <Modal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                type="success"
                title="Berhasil"
                message={successMessage}
            />

            <UnsealProgressModal
                isOpen={showSealModal}
                onClose={handleSealModalClose}
                onComplete={handleSealComplete}
                noTiket={sealTicketNo}
                namaIgt={sealNamaIgt}
                mode="seal"
            />
            </div>
        </DashboardLayout>
    );
};

export default DataAdmin;
